import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  BookOpen, Calendar, Clock, CheckCircle, AlertCircle, Users, X, Eye, Phone, Zap, MessageSquare, Wallet, User, Plus, FileText, Bot, Trophy,
  BarChart2, Award, Lock, Unlock, History, ArrowLeft
} from 'lucide-react';
import { getImageUrl } from '../utils/imageUtils';
import { CardThumbnail } from '../utils/cardUtils';

const ClassSizeInput = ({ unitId, minSize, onUpdate }) => {
  const [val, setVal] = useState(minSize);

  useEffect(() => {
    setVal(minSize);
  }, [minSize]);

  const handleBlur = () => {
    const num = parseInt(val);
    if (!isNaN(num) && num > 0 && num !== minSize) {
      onUpdate(unitId, num);
    }
  };

  return (
    <input
      type="number"
      min="1"
      className="w-16 p-1 text-right text-sm border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.currentTarget.blur();
        }
      }}
    />
  );
};

const LecturerUnits = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [assignedUnits, setAssignedUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [managedUnit, setManagedUnit] = useState(null);
  const [fetchingUnitStudents, setFetchingUnitStudents] = useState(false);
  const [allUnitStudents, setAllUnitStudents] = useState([]);
  const [installmentModalVisible, setInstallmentModalVisible] = useState(false);
  const [selectedStudentForInstallment, setSelectedStudentForInstallment] = useState(null);
  const [installmentAmount, setInstallmentAmount] = useState('');
  const [installmentMethod, setInstallmentMethod] = useState('cash');
  const [installmentNotes, setInstallmentNotes] = useState('');
  const [installmentReference, setInstallmentReference] = useState('');
  const [recordingInstallment, setRecordingInstallment] = useState(false);
  const [releasingFundsId, setReleasingFundsId] = useState(null);

  // ── Cohort Management State ────────────────────────────────────────────────
  const [cohortModalVisible, setCohortModalVisible] = useState(false);
  const [cohortModalUnit, setCohortModalUnit] = useState(null);
  const [cohortModalTab, setCohortModalTab] = useState('progress'); // 'progress' | 'history'
  const [activeCohort, setActiveCohort] = useState(null);       // current CohortRun
  const [cohortStudents, setCohortStudents] = useState([]);     // live progress board
  const [cohortHistory, setCohortHistory] = useState([]);       // past cohorts
  const [fetchingCohort, setFetchingCohort] = useState(false);
  const [graduatingCohort, setGraduatingCohort] = useState(false);
  const [togglingAdmissions, setTogglingAdmissions] = useState(false);
  const [startingCohort, setStartingCohort] = useState(false);
  const [showCreateCohort, setShowCreateCohort] = useState(false);
  const [newCohortForm, setNewCohortForm] = useState({ label: '', maxSize: 30, estimatedDurationDays: 30, autoAssignUnassigned: true });
  const [creatingCohort, setCreatingCohort] = useState(false);
  const [assigningStudentId, setAssigningStudentId] = useState(null);

  const isManageableAssignment = (unit) => ['accepted', 'active'].includes(unit?.assignmentStatus);
  const paidStatuses = ['confirmed', 'partial'];
  const isGraduatedStudent = (studentUnit) =>
    studentUnit?.cohortManagementStatus === 'graduated' ||
    studentUnit?.status === 'completed' ||
    (Array.isArray(studentUnit?.history) && studentUnit.history.some(h => h.graduatedAt));
  const getAssignableStudents = () =>
    allUnitStudents.filter(s => !s.cohortRunId && paidStatuses.includes(s.paymentStatus) && !isGraduatedStudent(s));

  // Decline modal state
  const [declineModalVisible, setDeclineModalVisible] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [selectedUnitForDecline, setSelectedUnitForDecline] = useState(null);
  const [submittingDecline, setSubmittingDecline] = useState(false);

  // Curriculum Management State
  const [curriculumModalVisible, setCurriculumModalVisible] = useState(false);
  const [curriculumItems, setCurriculumItems] = useState([]);
  const [selectedUnitForCurriculum, setSelectedUnitForCurriculum] = useState(null);
  const [fetchingCurriculum, setFetchingCurriculum] = useState(false);
  const [savingCurriculum, setSavingCurriculum] = useState(false);
  const [generatingAutomated, setGeneratingAutomated] = useState(false);
  const [uploadingCurriculum, setUploadingCurriculum] = useState(false);
  const [suggestedPlan, setSuggestedPlan] = useState(null);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiComplexityMode, setAiComplexityMode] = useState('comprehensive'); // 'compact' or 'comprehensive'
  const [explainingId, setExplainingId] = useState(null);
  const [generatingQuizId, setGeneratingQuizId] = useState(null);

  // Lesson Content Viewer / Editor State
  const [lessonContentModal, setLessonContentModal] = useState(null); // { item }
  const [editingContent, setEditingContent] = useState('');
  const [editMode, setEditMode] = useState(false); // false = view, true = edit
  const [refinementPrompt, setRefinementPrompt] = useState('');
  const [refiningContent, setRefiningContent] = useState(false);
  const [savingContent, setSavingContent] = useState(false);

  useEffect(() => {
    fetchAssignedUnits();
  }, [location.state?.selectedUnitId]);

  const fetchAssignedUnits = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lecturers/${user.id}/assigned-units`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch assigned units');
      }

      const data = await response.json();
      setAssignedUnits(data);

      // Handle navigation from dashboard with a specific unit selected
      if (location.state?.selectedUnitId) {
        const targetUnit = data.find(u => String(u.id) === String(location.state.selectedUnitId));
        if (targetUnit) {
          setManagedUnit(targetUnit);
          // Clear state to avoid reopening on refresh
          window.history.replaceState({}, document.title);
        }
      }
    } catch (error) {
      console.error('Error fetching assigned units:', error);
      setError('Failed to load assigned units. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateThreshold = async (unitId, newSize) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lecturers/update-threshold`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ unitId, minClassSize: newSize })
      });

      if (response.ok) {
        fetchAssignedUnits(); // Refresh to show new settings
      } else {
        alert('Failed to update class size');
      }
    } catch (err) {
      console.error('Error updating threshold:', err);
    }
  };

  const handleStartClass = async (unitId) => {
    if (!window.confirm('Are you sure you want to start this class? This will notify all enrolled students.')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lecturers/start-class`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ unitId })
      });

      if (response.ok) {
        alert('Class started successfully!');
        fetchAssignedUnits();
      } else {
        alert('Failed to start class');
      }
    } catch (err) {
      console.error('Error starting class:', err);
    }
  };

  const handleManageContent = (unit) => {
    // Navigate to content management for this unit
    navigate(`/learning-materials?unitId=${unit.id}`, { state: { unit } });
  };

  const handleEndClass = async (unitId) => {
    if (!window.confirm('Are you sure you want to end this class? This will mark all current students as completed and reset the pool for new enrollments.')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lecturers/end-class`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ unitId })
      });

      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        fetchAssignedUnits(); // Refresh to show reset status
      } else {
        const err = await response.json();
        alert(err.message || 'Failed to end class');
      }
    } catch (err) {
      console.error('Error ending class:', err);
      alert('Network error while ending class');
    }
  };

  const fetchCurriculum = async (unitId) => {
    try {
      setFetchingCurriculum(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lesson-plans/${unitId}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) {
        setCurriculumItems(await response.json());
      }
    } catch (err) {
      console.error('Error fetching curriculum:', err);
    } finally {
      setFetchingCurriculum(false);
    }
  };

  const handleUploadCurriculum = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('curriculum', file);
    formData.append('unitId', selectedUnitForCurriculum.id);
    formData.append('complexity', aiComplexityMode);

    try {
      setUploadingCurriculum(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ai/curriculum/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        alert('Curriculum uploaded and analyzed successfully!');
        if (selectedUnitForCurriculum) {
          fetchCurriculum(selectedUnitForCurriculum.id);
        }
      } else {
        const err = await response.json();
        alert(err.message || 'Failed to upload curriculum');
      }
    } catch (err) {
      console.error('Error uploading curriculum:', err);
      alert('Network error while uploading curriculum');
    } finally {
      setUploadingCurriculum(false);
      // Reset input
      if (e.target) e.target.value = '';
    }
  };

  const handleAICurriculum = async (unitId) => {
    if (!aiPrompt || aiPrompt.length < 5) {
      alert("Please provide a valid topic prompt.");
      return;
    }

    try {
      setGeneratingAutomated(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ai/curriculum/generate-from-prompt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ unitId, prompt: aiPrompt, complexity: aiComplexityMode })
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message);
        setAiPrompt('');
        fetchCurriculum(unitId);
      } else {
        const err = await response.json();
        alert(err.message || 'Failed to generate automated course');
      }
    } catch (err) {
      console.error(err);
      alert('Error generating automated course');
    } finally {
      setGeneratingAutomated(false);
    }
  };

  const handleApplyAIPlan = async () => {
    if (!suggestedPlan || !suggestedPlan.steps) return;

    try {
      setSavingCurriculum(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ai/lesson-plan/apply`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          unitId: selectedUnitForCurriculum.id,
          plan: suggestedPlan.steps
        })
      });

      if (response.ok) {
        alert('Lesson plan applied successfully!');
        setSuggestedPlan(null);
        fetchCurriculum(selectedUnitForCurriculum.id);
      } else {
        alert('Failed to apply plan');
      }
    } catch (err) {
      console.error(err);
      alert('Error applying plan');
    } finally {
      setSavingCurriculum(false);
    }
  };

  const handleAIExplain = async (lessonItemId) => {
    try {
      setExplainingId(lessonItemId);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ai/lesson/explain`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ lessonItemId })
      });

      if (response.ok) {
        const data = await response.json();
        // Now get Polly audio for this explanation
        const pollyRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ai/speech/generate`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          },
          body: JSON.stringify({ text: data.explanation })
        });

        if (pollyRes.ok) {
          const blob = await pollyRes.blob();
          const url = URL.createObjectURL(blob);
          const audio = new Audio(url);
          audio.onended = () => setExplainingId(null);
          audio.play();
        } else {
          setExplainingId(null);
        }
      } else {
        setExplainingId(null);
      }
    } catch (err) {
      console.error(err);
      setExplainingId(null);
    }
  };

  const handleGenerateAIQuiz = async (lessonItemId) => {
    if (!window.confirm('Generate a Smart Quiz for this lesson? This will create 5 questions based on your curriculum.')) return;

    try {
      setGeneratingQuizId(lessonItemId);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/ai/quiz/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ lessonItemId })
      });

      if (response.ok) {
        alert('Smart Quiz generated and attached successfully!');
        const unitId = selectedUnitForCurriculum.id;
        fetchCurriculum(unitId); // Refresh to show the new quiz
      } else {
        alert('Failed to generate Smart Quiz');
      }
    } catch (err) {
      console.error(err);
      alert('Error generating Smart Quiz');
    } finally {
      setGeneratingQuizId(null);
    }
  };

  const handleAddCurriculumItem = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const newItem = {
      unitId: selectedUnitForCurriculum.id,
      title: formData.get('title'),
      description: formData.get('description'),
      type: formData.get('type'),
      plannedDate: formData.get('plannedDate') || null,
      order: curriculumItems.length + 1
    };

    try {
      setSavingCurriculum(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lesson-plans`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(newItem)
      });

      if (response.ok) {
        e.target.reset();
        fetchCurriculum(selectedUnitForCurriculum.id);
      }
    } catch (err) {
      console.error('Error adding curriculum item:', err);
    } finally {
      setSavingCurriculum(false);
    }
  };

  const handleDeleteCurriculumItem = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lesson-plans/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.ok) fetchCurriculum(selectedUnitForCurriculum.id);
    } catch (err) {
      console.error('Error deleting item:', err);
    }
  };

  const handleToggleCompletion = async (itemId, isCompleted) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lesson-plans/${itemId}/toggle-completion`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ isCompleted })
      });
      if (response.ok) {
        fetchCurriculum(selectedUnitForCurriculum.id);
      }
    } catch (err) {
      console.error('Error toggling completion:', err);
    }
  };

  const handleStartLesson = async (itemId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lesson-plans/${itemId}/start-lesson`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        if (response.status === 400 && errorData.currentStatus) {
          alert(`Cannot start lesson: ${errorData.message}\n\nCurrent class status: ${errorData.currentStatus}\n\nPlease start the class first using the "Start Class" button.`);
        } else {
          alert(errorData.message || 'Failed to start lesson');
        }
        return;
      }

      fetchCurriculum(selectedUnitForCurriculum.id);
    } catch (err) {
      console.error('Error starting lesson:', err);
      alert('Failed to start lesson. Please try again.');
    }
  };

  const handleEndLesson = async (itemId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lesson-plans/${itemId}/end-lesson`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      if (response.ok) {
        fetchCurriculum(selectedUnitForCurriculum.id);
      }
    } catch (err) {
      console.error('Error ending lesson:', err);
    }
  };

  const handleViewLessonContent = (item) => {
    setLessonContentModal(item);
    setEditingContent(item.description || '');
    setEditMode(false);
    setRefinementPrompt('');
  };

  const handleSaveContent = async () => {
    if (!lessonContentModal) return;
    try {
      setSavingContent(true);
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lesson-plans/${lessonContentModal.id}/content`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ description: editingContent })
      });
      if (res.ok) {
        const updated = await res.json();
        setLessonContentModal(updated);
        setEditMode(false);
        fetchCurriculum(selectedUnitForCurriculum.id);
      } else {
        alert('Failed to save content.');
      }
    } catch (err) {
      alert('Network error saving content.');
    } finally {
      setSavingContent(false);
    }
  };

  const handleAIRefineContent = async () => {
    if (!lessonContentModal || !refinementPrompt.trim()) return;
    try {
      setRefiningContent(true);
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lesson-plans/${lessonContentModal.id}/ai-refine`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ instruction: refinementPrompt })
      });
      if (res.ok) {
        const data = await res.json();
        setEditingContent(data.content);
        setLessonContentModal(data.item);
        setRefinementPrompt('');
        fetchCurriculum(selectedUnitForCurriculum.id);
      } else {
        const err = await res.json();
        alert(err.message || 'Automated refinement failed.');
      }
    } catch (err) {
      alert('Network error during automated refinement.');
    } finally {
      setRefiningContent(false);
    }
  };

  const handleReleaseFunds = async (studentId, unitId) => {
    if (!window.confirm('Are you sure you want to mark this unit as completed for this student? This will release your commission for withdrawal.')) {
      return;
    }

    try {
      setReleasingFundsId(`${studentId}-${unitId}`);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/earnings/release-unit`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ studentId, unitId })
      });

      if (response.ok) {
        const result = await response.json();
        alert(result.message || 'Funds released successfully!');
        if (cohortModalVisible) openCohortModal(cohortModalUnit);
      } else {
        const err = await response.json();
        alert(err.message || 'Failed to release funds');
      }
    } catch (err) {
      console.error('Error releasing funds:', err);
      alert('Network error');
    } finally {
      setReleasingFundsId(null);
    }
  };

  const handleFinalizeClass = async (unitId) => {
    if (!window.confirm('Are you sure you want to finalize this class? This will graduate all students.')) {
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lecturers/finalize-class`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ unitId })
      });

      if (response.ok) {
        const result = await response.json();
        alert(result.message);
        fetchAssignedUnits(); // Refresh
      } else {
        const err = await response.json();
        alert(err.message || 'Failed to finalize class');
      }
    } catch (err) {
      console.error('Error finalizing class:', err);
      alert('Network error');
    }
  };

  // ── Cohort API Handlers ──────────────────────────────────────────────────
  const openCohortModal = async (unit) => {
    setCohortModalUnit(unit);
    setCohortModalTab('progress');
    setCohortModalVisible(true);
    setFetchingCohort(true);
    setFetchingUnitStudents(true);
    try {
      const headers = { 'Authorization': `Bearer ${localStorage.getItem('token')}` };
      
      // 1. Fetch active cohort for this unit
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/cohorts/my-cohorts?unitId=${unit.id}`, { headers });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'You are no longer assigned to manage this unit.');
      }
      const cohorts = await res.json();
      const active = cohorts.find(c => c.status !== 'archived') || null;
      setActiveCohort(active);

      // 2. Fetch all unit students (the "Master List")
      const sAllRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/cohorts/unit/${unit.id}/all-students`, { headers });
      if (!sAllRes.ok) {
        const err = await sAllRes.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to fetch unit students.');
      }
      const sAllData = await sAllRes.json();
      setAllUnitStudents(sAllData || []);

      if (active) {
        // 3. Fetch students with progress for this specific cohort
        const sRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/cohorts/${active.id}/progress`, { headers });
        if (!sRes.ok) {
          const err = await sRes.json().catch(() => ({}));
          throw new Error(err.message || 'Failed to fetch cohort progress.');
        }
        const sData = await sRes.json();
        setCohortStudents(sData.students || []);
      } else {
        setCohortStudents([]);
      }

      // 4. Fetch history
      const hRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/cohorts/unit/${unit.id}/history`, { headers });
      if (!hRes.ok) {
        const err = await hRes.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to fetch cohort history.');
      }
      setCohortHistory(await hRes.json());
    } catch (err) {
      console.error('Error fetching cohort data:', err);
      alert(err.message || 'Unable to open cohort manager.');
      setCohortModalVisible(false);
    } finally {
      setFetchingCohort(false);
      setFetchingUnitStudents(false);
    }
  };

  const handleApproveCashPayment = async (studentId, unitId) => {
    if (!window.confirm('Confirm receipt of cash payment?')) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/payments/approve-cash/${studentId}/${unitId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ lecturerId: user.id, approvalNotes: 'Approved by lecturer via cohort manager' })
      });
      if (response.ok) {
        alert('Payment approved successfully!');
        openCohortModal(cohortModalUnit); // Refresh data
      } else {
        const err = await response.json();
        alert(err.message || 'Failed to approve payment');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleRejectCashPayment = async (studentId, unitId) => {
    const reason = prompt('Please provide a reason for rejection:');
    if (reason === null) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/payments/reject-cash/${studentId}/${unitId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ lecturerId: user.id, rejectionNotes: reason || 'Rejected by lecturer' })
      });
      if (response.ok) {
        alert('Payment rejected.');
        openCohortModal(cohortModalUnit); // Refresh data
      } else {
        const err = await response.json();
        alert(err.message || 'Failed to reject payment');
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateCohort = async () => {
    if (!cohortModalUnit) return;
    if (activeCohort) {
      alert('Archive the current cohort before creating another one for this unit.');
      return;
    }
    setCreatingCohort(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/cohorts`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ unitId: cohortModalUnit.id, ...newCohortForm })
      });
      if (res.ok) {
        const data = await res.json();
        setShowCreateCohort(false);
        setNewCohortForm({ label: '', maxSize: 30, estimatedDurationDays: 30, autoAssignUnassigned: true });
        if (data.autoAssignedCount > 0) {
          alert(`Cohort created! ${data.autoAssignedCount} student(s) were automatically assigned.`);
        }
        await openCohortModal(cohortModalUnit);
      } else {
        const err = await res.json();
        alert(err.message || 'Failed to create cohort');
      }
    } catch (err) {
      alert('Network error');
    } finally {
      setCreatingCohort(false);
    }
  };

  const handleStartCohort = async (closeAdmissions = false) => {
    if (!activeCohort) return;
    setStartingCohort(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/cohorts/${activeCohort.id}/start`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ closeAdmissions })
      });
      if (res.ok) {
        await openCohortModal(cohortModalUnit);
        fetchAssignedUnits(); // Refresh the grid status
      } else {
        const err = await res.json();
        alert(err.message);
      }
    } catch (err) {
      alert('Network error');
    } finally {
      setStartingCohort(false);
    }
  };

  const handleToggleAdmissions = async () => {
    if (!activeCohort) return;
    setTogglingAdmissions(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/cohorts/${activeCohort.id}/admissions`, {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ admissionsOpen: !activeCohort.admissionsOpen })
      });
      if (res.ok) {
        setActiveCohort(prev => ({ ...prev, admissionsOpen: !prev.admissionsOpen }));
      } else {
        const err = await res.json();
        alert(err.message);
      }
    } catch (err) {
      alert('Network error');
    } finally {
      setTogglingAdmissions(false);
    }
  };

  const handleGraduateCohort = async () => {
    if (!activeCohort) return;
    if (!window.confirm(`Graduate & archive Cohort #${activeCohort.cohortNumber}? This will snapshot all students and release their earnings. The live view will be cleared for your next cohort.`)) return;
    setGraduatingCohort(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/cohorts/${activeCohort.id}/graduate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        const result = await res.json();
        alert(result.message);
        await openCohortModal(cohortModalUnit);
        fetchAssignedUnits();
      } else {
        const err = await res.json();
        alert(err.message);
      }
    } catch (err) {
      alert('Network error');
    } finally {
      setGraduatingCohort(false);
    }
  };

  const handleAssignStudent = async (studentId, studentName) => {
    if (!activeCohort) {
      alert('No active cohort to assign to. Please create a cohort first.');
      return;
    }
    const studentUnit = allUnitStudents.find(s => s.studentId === studentId);
    if (isGraduatedStudent(studentUnit)) {
      alert(`${studentName} has already graduated from this unit.`);
      return;
    }
    if (!window.confirm(`Assign ${studentName} to Cohort #${activeCohort.cohortNumber}?`)) return;
    setAssigningStudentId(studentId);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/cohorts/${activeCohort.id}/add-student`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ studentId })
      });
      const data = await res.json();
      if (res.ok) {
        alert(`${studentName} assigned to Cohort #${activeCohort.cohortNumber} successfully.`);
        await openCohortModal(cohortModalUnit); // Refresh
      } else {
        alert(data.message || 'Failed to assign student');
      }
    } catch (err) {
      alert('Network error');
    } finally {
      setAssigningStudentId(null);
    }
  };

  const renderManagedUnitScreen = (unit) => {
    if (!isManageableAssignment(unit)) {
      return (
        <div className="space-y-6">
          <div className="flex items-start gap-3">
            <button
              onClick={() => setManagedUnit(null)}
              className="mt-1 p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
              title="Back to all assigned units"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-amber-600">Assignment Pending</p>
              <h1 className="text-2xl font-bold text-gray-900">{unit.name}</h1>
              <p className="text-gray-600 mt-1">Accept this assignment before managing cohorts, content, or materials.</p>
            </div>
          </div>

          <div className="bg-white rounded-xl border border-amber-100 p-6 shadow-sm max-w-2xl">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 mt-0.5" />
              <div className="flex-1">
                <h2 className="font-bold text-gray-900 mb-1">Management is unavailable</h2>
                <p className="text-sm text-gray-600 mb-4">
                  This unit is not currently active for you. Cohorts, curriculum, and material uploads stay locked until the assignment is accepted.
                </p>
                {unit.assignmentStatus === 'pending' && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleAcceptAssignment(unit.id, unit.campusId || unit.lecturer?.campusId)}
                      className="btn-success"
                    >
                      Accept Assignment
                    </button>
                    <button
                      onClick={() => handleDeclineAssignment(unit)}
                      className="btn-error"
                    >
                      Decline
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    const isSelfPaced = unit.learningMode === 'self_paced';
    const isForming = unit.currentCohortStatus === 'forming';
    const enrolledCount = unit.enrolledStudentsCount || 0;
    const minClassSize = unit.minClassSize || 1;
    const canStart = enrolledCount >= minClassSize;

    return (
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-start gap-3">
            <button
              onClick={() => setManagedUnit(null)}
              className="mt-1 p-2 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-50 transition-colors"
              title="Back to all assigned units"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">Unit Management</p>
              <h1 className="text-2xl font-bold text-gray-900">{unit.name}</h1>
              <p className="text-gray-600 mt-1">{unit.course?.name || unit.campusName || 'Assigned unit'}</p>
            </div>
          </div>
          <span className={`self-start sm:self-center status-badge ${getUnitStatusColor(unit.assignmentStatus || unit.status)}`}>
            {getStatusText(unit.assignmentStatus || unit.status)}
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 overflow-hidden shadow-sm">
            <CardThumbnail name={unit.name} imageUrl={getImageUrl(unit.imageUrl)} className="w-full h-56" />
            <div className="p-6">
              <h2 className="font-bold text-gray-900 mb-2">Overview</h2>
              <p className="text-sm text-gray-600 leading-relaxed">
                {unit.description || 'No description available for this unit.'}
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-5">
                <div className="rounded-lg bg-indigo-50 border border-indigo-100 p-3">
                  <p className="text-xs text-indigo-600 font-bold uppercase">Students</p>
                  <p className="text-xl font-black text-indigo-900">{enrolledCount}</p>
                </div>
                <div className="rounded-lg bg-green-50 border border-green-100 p-3">
                  <p className="text-xs text-green-600 font-bold uppercase">Class Size</p>
                  <p className="text-xl font-black text-green-900">{minClassSize}</p>
                </div>
                <div className="rounded-lg bg-purple-50 border border-purple-100 p-3">
                  <p className="text-xs text-purple-600 font-bold uppercase">Mode</p>
                  <p className="text-sm font-black text-purple-900">{isSelfPaced ? 'Self-paced' : 'Live class'}</p>
                </div>
                <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
                  <p className="text-xs text-gray-500 font-bold uppercase">Schedule</p>
                  <p className="text-sm font-black text-gray-900">{unit.classDate ? new Date(unit.classDate).toLocaleDateString() : 'Flexible'}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={() => openCohortModal(unit)}
              className="w-full rounded-xl border border-indigo-100 bg-indigo-50 p-4 text-left hover:bg-indigo-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <BarChart2 className="w-5 h-5 text-indigo-600" />
                <div>
                  <p className="font-bold text-indigo-900">Cohorts & Students</p>
                  <p className="text-xs text-indigo-600">Manage cohorts, payments, progress, and releases.</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => {
                setSelectedUnitForCurriculum(unit);
                fetchCurriculum(unit.id);
                setCurriculumModalVisible(true);
              }}
              className="w-full rounded-xl border border-blue-100 bg-blue-50 p-4 text-left hover:bg-blue-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <Zap className="w-5 h-5 text-blue-600" />
                <div>
                  <p className="font-bold text-blue-900">Curriculum & Lessons</p>
                  <p className="text-xs text-blue-600">Plan lessons, generate content, and attach resources.</p>
                </div>
              </div>
            </button>

            <button
              onClick={() => handleManageContent(unit)}
              className="w-full rounded-xl border border-purple-100 bg-purple-50 p-4 text-left hover:bg-purple-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <BookOpen className="w-5 h-5 text-purple-600" />
                <div>
                  <p className="font-bold text-purple-900">Learning Materials</p>
                  <p className="text-xs text-purple-600">Upload and manage unit content.</p>
                </div>
              </div>
            </button>

            {isForming ? (
              <button
                onClick={() => handleStartClass(unit.id)}
                disabled={!canStart}
                className="w-full rounded-xl border border-green-100 bg-green-600 p-4 text-left text-white hover:bg-green-700 transition-colors disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5" />
                  <div>
                    <p className="font-bold">Start Class</p>
                    <p className="text-xs opacity-80">{canStart ? 'Minimum class size reached.' : `${enrolledCount}/${minClassSize} students enrolled.`}</p>
                  </div>
                </div>
              </button>
            ) : unit.currentCohortStatus === 'active' ? (
              <button
                onClick={() => handleEndClass(unit.id)}
                className="w-full rounded-xl border border-red-100 bg-red-50 p-4 text-left hover:bg-red-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-red-600" />
                  <div>
                    <p className="font-bold text-red-900">End Class</p>
                    <p className="text-xs text-red-600">Graduate students and close the active engagement.</p>
                  </div>
                </div>
              </button>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const renderSelfPacedUnitCard = (unit) => {
    const isForming = unit.currentCohortStatus === 'forming';
    const progress = Math.min(100, Math.round((unit.enrolledStudentsCount / unit.minClassSize) * 100));
    const canStart = unit.enrolledStudentsCount >= unit.minClassSize;

    return (
      <div
        key={unit.id}
        onClick={() => isManageableAssignment(unit) && setManagedUnit(unit)}
        className={`border border-gray-200 rounded-lg overflow-hidden hover:shadow-md transition-shadow bg-white flex flex-col ${isManageableAssignment(unit) ? 'cursor-pointer' : ''}`}
      >
        <CardThumbnail name={unit.name} imageUrl={getImageUrl(unit.imageUrl)} className="h-40" />
        <div className="p-5">
          <div className="flex justify-between items-start mb-4">
            <div>
              <span className="text-xs font-semibold tracking-wide text-blue-600 uppercase">Subject Pool</span>
              <h3 className="text-xl font-bold text-gray-900 mt-1">{unit.name}</h3>
              <p className="text-gray-600 text-sm">{unit.course?.name}</p>
            </div>
            <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${unit.currentCohortStatus === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
              }`}>
              {unit.currentCohortStatus === 'active' ? 'Class Active' : 'Forming Cohort'}
            </span>
          </div>

          {/* Cohort Progress Section */}
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="flex justify-between items-end mb-2">
              <div>
                <p className="text-2xl font-bold text-gray-900">{unit.enrolledStudentsCount} <span className="text-sm font-normal text-gray-500">enrolled</span></p>
              </div>
              <div className="text-right">
                <label className="text-xs text-gray-500 block mb-1">{isForming ? 'Target Class Size' : 'Cohort Max Size'}</label>
                <ClassSizeInput
                  unitId={unit.id}
                  minSize={unit.minClassSize}
                  onUpdate={handleUpdateThreshold}
                />
              </div>
            </div>

            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-2.5 mb-1">
              <div
                className={`h-2.5 rounded-full transition-all duration-500 ${
                  unit.currentCohortStatus === 'active' ? 'bg-indigo-600' :
                  canStart ? 'bg-green-600' : 'bg-blue-600'
                }`}
                style={{ width: `${unit.currentCohortStatus === 'active' ? (unit.avgProgress || 100) : progress}%` }}
              ></div>
            </div>
            <p className="text-xs text-gray-500 text-right">
              {unit.currentCohortStatus === 'active' 
                ? `Average Class Progress: ${unit.avgProgress || 0}%` 
                : `${progress}% of target reached`}
            </p>
          </div>

          {/* Consensus / Voting Dashboard */}
          {
            unit.currentCohortStatus === 'pending_conclusion' && unit.consensus && (
              <div className="mb-4 bg-purple-50 p-4 rounded-lg border border-purple-100">
                <h4 className="font-semibold text-purple-900 flex items-center mb-2">
                  <AlertCircle className="w-4 h-4 mr-2" />
                  Class Closure Review
                </h4>
                <div className="flex justify-between text-sm mb-3">
                  <span className="text-gray-600">Total Enrolled: {unit.enrolledStudentsCount}</span>
                  <span className="text-gray-600">Votes Cast: {unit.consensus.total}</span>
                </div>
                <div className="flex gap-2 mb-3">
                  <div className="flex-1 bg-white p-2 rounded shadow-sm border border-green-100 text-center">
                    <span className="block text-xl font-bold text-green-600">{unit.consensus.granted}</span>
                    <span className="text-xs text-gray-500">Granted</span>
                  </div>
                  <div className="flex-1 bg-white p-2 rounded shadow-sm border border-red-100 text-center">
                    <span className="block text-xl font-bold text-red-600">{unit.consensus.rejected}</span>
                    <span className="text-xs text-gray-500">Rejected</span>
                  </div>
                </div>

                {unit.consensus.rejected > 0 && (
                  <div className="mb-3">
                    <p className="text-xs font-semibold text-red-800 mb-1">Students Requesting Changes:</p>
                    <div className="max-h-40 overflow-y-auto space-y-2">
                      {unit.consensus.rejectors ? (
                        unit.consensus.rejectors.map((student, idx) => (
                          <div key={idx} className="bg-white p-2 rounded border border-red-100 flex justify-between items-start text-xs">
                            <div>
                              <span className="font-bold text-gray-800 block">{student.name}</span>
                              <span className="text-gray-600 block italic">"{student.reason}"</span>
                            </div>
                            {student.phoneNumber && (
                              <a
                                href={`https://wa.me/${student.phoneNumber.replace(/\D/g, '')}`}
                                target="_blank"
                                rel="noreferrer"
                                className="ml-2 text-green-600 hover:text-green-700 bg-green-50 p-1.5 rounded flex items-center gap-1"
                                title="Contact on WhatsApp"
                              >
                                <Phone className="w-3 h-3" />
                                <span>Chat</span>
                              </a>
                            )}
                          </div>
                        ))
                      ) : (
                        // Fallback for backward compatibility
                        <ul className="text-xs text-gray-600 list-disc list-inside bg-white p-2 rounded">
                          {unit.consensus.reasons.map((reason, idx) => (
                            <li key={idx}>{reason}</li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                )}

                <button
                  onClick={() => handleFinalizeClass(unit.id)}
                  className="w-full py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors font-medium text-sm"
                >
                  Finalize & Graduate Class
                </button>
              </div>
            )
          }

          {isManageableAssignment(unit) && (
            <div className="flex gap-3 mt-4 pt-4 border-t border-gray-100">
              {isForming ? (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStartClass(unit.id);
                  }}
                  disabled={!canStart}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${canStart
                    ? 'bg-green-600 text-white hover:bg-green-700 shadow-sm'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                >
                  <CheckCircle className="w-4 h-4" />
                  Start Class
                </button>
              ) : (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setManagedUnit(unit);
                  }}
                  className="flex-1 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                >
                  <BookOpen className="w-4 h-4" />
                  Manage Class
                </button>
              )}

              {unit.currentCohortStatus === 'active' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEndClass(unit.id);
                  }}
                  className="px-4 py-2 border border-red-200 text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors flex items-center justify-center gap-2"
                  title="End engagement and graduate students"
                >
                  <CheckCircle className="w-4 h-4" />
                  End Class
                </button>
              )}
            </div>
          )}

          {/* Cohort Management Button */}
          {isManageableAssignment(unit) && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  openCohortModal(unit);
                }}
                className="w-full mt-3 py-2 bg-indigo-50 text-indigo-700 rounded-lg border border-indigo-100 hover:bg-indigo-100 transition-all font-medium text-sm flex items-center justify-center gap-2 group"
              >
                <BarChart2 className="w-4 h-4 text-indigo-500 group-hover:scale-110 transition-transform" />
                Manage Cohorts & Progress
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedUnitForCurriculum(unit);
                  fetchCurriculum(unit.id);
                  setCurriculumModalVisible(true);
                }}
                className="w-full mt-3 py-2 bg-blue-50 text-blue-700 rounded-lg border border-blue-100 hover:bg-blue-100 transition-all font-medium text-sm flex items-center justify-center gap-2 group"
              >
                <Zap className="w-4 h-4 text-yellow-500 group-hover:scale-110 transition-transform" />
                Manage Curriculum & Lesson Plan
              </button>
            </>
          )}
        </div>
      </div>
    );
  };

  const handleAcceptAssignment = async (unitId, campusId) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lecturers/${user.id}/units/${unitId}/accept-assignment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ campusId }),
      });

      const data = await response.json();
      if (response.ok) {
        alert('Assignment accepted successfully!');
        fetchAssignedUnits();
      } else {
        alert(data.message || 'Failed to accept assignment.');
      }
    } catch (error) {
      console.error('Error accepting assignment:', error);
      alert('Failed to accept assignment. Please try again.');
    }
  };

  const handleDeclineAssignment = (unit) => {
    setSelectedUnitForDecline(unit);
    setDeclineReason('');
    setDeclineModalVisible(true);
  };

  const submitDecline = async () => {
    if (!declineReason.trim()) {
      alert('Please provide a reason for declining the assignment.');
      return;
    }

    if (declineReason.trim().length < 10) {
      alert('Decline reason must be at least 10 characters long.');
      return;
    }

    setSubmittingDecline(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/lecturers/${user.id}/units/${selectedUnitForDecline.id}/decline-assignment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ declineReason: declineReason.trim() }),
      });

      const data = await response.json();
      if (response.ok) {
        alert('Assignment declined successfully!');
        setDeclineModalVisible(false);
        fetchAssignedUnits();
      } else {
        alert(data.message || 'Failed to decline assignment.');
      }
    } catch (error) {
      console.error('Error declining assignment:', error);
      alert('Failed to decline assignment. Please try again.');
    } finally {
      setSubmittingDecline(false);
    }
  };

  // Removed old student management functions


  const handleRecordInstallment = async () => {
    if (!installmentAmount || parseFloat(installmentAmount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    setRecordingInstallment(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/payments/record-installment`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          studentId: selectedStudentForInstallment.studentId,
          unitId: selectedStudentForInstallment.unitId,
          amount: parseFloat(installmentAmount),
          paymentMethod: installmentMethod,
          paymentNotes: installmentNotes,
          referenceNumber: installmentReference
        })
      });

      if (response.ok) {
        alert('Installment recorded successfully!');
        setInstallmentModalVisible(false);
        setInstallmentAmount('');
        setInstallmentNotes('');
        setInstallmentReference('');
        // Refresh cohort modal data
        if (cohortModalVisible) openCohortModal(cohortModalUnit);
      } else {
        const errorData = await response.json();
        alert(errorData.message || 'Failed to record installment');
      }
    } catch (err) {
      console.error('Error recording installment:', err);
      alert('Error recording installment');
    } finally {
      setRecordingInstallment(false);
    }
  };

  const getUnitStatusColor = (status) => {
    switch (status) {
      case 'accepted':
        return 'text-green-600 bg-green-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'active':
        return 'text-green-600 bg-green-100';
      case 'pending_acceptance':
        return 'text-yellow-600 bg-yellow-100';
      case 'completed':
        return 'text-blue-600 bg-blue-100';
      case 'upcoming':
        return 'text-purple-600 bg-purple-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'accepted':
        return 'Active';
      case 'pending':
        return 'Waiting for Acceptance';
      case 'active':
        return 'Ongoing';
      case 'pending_acceptance':
        return 'Waiting for Acceptance';
      case 'completed':
        return 'Completed';
      case 'upcoming':
        return 'Upcoming';
      default:
        return status;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-error-50 border border-error-200 rounded-lg p-6 text-center">
        <AlertCircle className="w-12 h-12 text-error-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-error-800 mb-2">Error Loading Units</h3>
        <p className="text-error-700 mb-4">{error}</p>
        <button
          onClick={fetchAssignedUnits}
          className="btn-primary"
        >
          Try Again
        </button>
      </div>
    );
  }

  const getLecturerSummary = () => {
    const totalUnits = assignedUnits.length;
    const pendingUnits = assignedUnits.filter(unit => unit.assignmentStatus === 'pending').length;
    const activeUnits = assignedUnits.filter(unit => isManageableAssignment(unit)).length;
    const completedUnits = assignedUnits.filter(unit => unit.status === 'completed').length;

    return { totalUnits, pendingUnits, activeUnits, completedUnits };
  };

  const lecturerSummary = getLecturerSummary();

  return (
    <div className="space-y-6">
      {managedUnit ? (
        renderManagedUnitScreen(managedUnit)
      ) : (
        <>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">📚 Assigned Units</h1>
          <p className="text-gray-600 mt-1">Manage your teaching assignments</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm">Total Units</p>
              <p className="text-2xl font-bold">{lecturerSummary.totalUnits}</p>
            </div>
            <BookOpen className="w-8 h-8 text-blue-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-yellow-100 text-sm">Pending</p>
              <p className="text-2xl font-bold">{lecturerSummary.pendingUnits}</p>
            </div>
            <Clock className="w-8 h-8 text-yellow-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm">Active</p>
              <p className="text-2xl font-bold">{lecturerSummary.activeUnits}</p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-200" />
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm">Completed</p>
              <p className="text-2xl font-bold">{lecturerSummary.completedUnits}</p>
            </div>
            <Users className="w-8 h-8 text-purple-200" />
          </div>
        </div>
      </div>

      {/* Units List */}
      {assignedUnits.length > 0 ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {assignedUnits.map((unit) => {
            if (unit.learningMode === 'self_paced') {
              return renderSelfPacedUnitCard(unit);
            }
            return (
              <div
                key={unit.id}
                onClick={() => isManageableAssignment(unit) && setManagedUnit(unit)}
                className={`card p-0 overflow-hidden hover:shadow-large transition-all duration-300 flex flex-col ${isManageableAssignment(unit) ? 'cursor-pointer' : ''}`}
              >
                <CardThumbnail name={unit.name} imageUrl={getImageUrl(unit.imageUrl)} className="h-40" />
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <BookOpen className="w-6 h-6 text-blue-600" />
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 mb-1">
                          {unit.name}
                        </h3>
                        <p className="text-sm text-gray-600">
                          {unit.course?.name || 'Course not assigned'}
                        </p>
                      </div>
                    </div>
                    <span className={`status-badge ${getUnitStatusColor(unit.assignmentStatus || unit.status)}`}>
                      {getStatusText(unit.assignmentStatus || unit.status)}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="flex items-center space-x-2">
                      <Users className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-700">
                        {unit.enrolledStudentsCount || 0} students
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-700">
                        {unit.classDate ? new Date(unit.classDate).toLocaleDateString() : 'No date set'}
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-2">
                    {unit.assignmentStatus === 'pending' && (
                      <>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAcceptAssignment(unit.id, unit.campusId || unit.lecturer?.campusId);
                          }}
                          className="btn-success flex-1"
                        >
                          ✓ Accept
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeclineAssignment(unit);
                          }}
                          className="btn-error flex-1"
                        >
                          ✗ Decline
                        </button>
                      </>
                    )}

                    {isManageableAssignment(unit) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setManagedUnit(unit);
                        }}
                        className="btn-primary flex-1 flex items-center justify-center gap-2"
                      >
                        <BookOpen className="w-4 h-4" /> Manage Unit
                      </button>
                    )}

                    {unit.status === 'completed' && (
                      <div className="text-center py-2 text-green-600 font-medium">
                        ✓ Completed
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No units assigned</h3>
          <p className="text-gray-600">New assignments will appear here</p>
        </div>
      )}
        </>
      )}

      {/* Decline Modal */}
      {declineModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Decline Assignment</h3>
              <button
                onClick={() => setDeclineModalVisible(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reason for declining "{selectedUnitForDecline?.name}"
                </label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-500"
                  rows={4}
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  placeholder="Please provide a detailed reason..."
                  maxLength={500}
                />
                <p className="text-xs text-gray-500 mt-1">
                  {declineReason.length}/500 characters (minimum 10)
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setDeclineModalVisible(false)}
                  className="flex-1 bg-gray-500 text-white py-2 px-4 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={submitDecline}
                  disabled={submittingDecline}
                  className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {submittingDecline ? 'Submitting...' : 'Submit Decline'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Removed old Students Modal */}

      {/* Record Installment Modal */}
      {installmentModalVisible && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 shadow-2xl">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-lg font-bold text-gray-900">Record Installment</h3>
              <button
                onClick={() => setInstallmentModalVisible(false)}
                className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-full"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 mb-4">
                <p className="text-xs text-blue-700 font-bold uppercase tracking-wider mb-1">Student</p>
                <p className="font-bold text-blue-900">{selectedStudentForInstallment?.student?.name}</p>
                <div className="flex justify-between mt-2 pt-2 border-t border-blue-200">
                  <span className="text-xs text-blue-600">Balance:</span>
                  <span className="text-sm font-bold text-blue-900">
                    KES {((parseFloat(selectedStudentForInstallment?.totalAmount) || 0) - (parseFloat(selectedStudentForInstallment?.paidAmount) || 0)).toLocaleString()}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Amount (KES)</label>
                <input
                  type="number"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none font-bold"
                  value={installmentAmount}
                  onChange={(e) => setInstallmentAmount(e.target.value)}
                  placeholder="Enter amount paid"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Payment Method</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setInstallmentMethod('cash')}
                    className={`py-2 px-3 rounded-lg text-sm font-bold border transition-all ${installmentMethod === 'cash' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'}`}
                  >
                    Cash
                  </button>
                  <button
                    onClick={() => setInstallmentMethod('mpesa')}
                    className={`py-2 px-3 rounded-lg text-sm font-bold border transition-all ${installmentMethod === 'mpesa' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'}`}
                  >
                    M-Pesa / Mobile
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Reference # (Optional)</label>
                <input
                  type="text"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                  value={installmentReference}
                  onChange={(e) => setInstallmentReference(e.target.value)}
                  placeholder="Receipt or Trans. ID"
                />
              </div>

              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Notes (Optional)</label>
                <textarea
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none"
                  rows={2}
                  value={installmentNotes}
                  onChange={(e) => setInstallmentNotes(e.target.value)}
                  placeholder="Any additional details..."
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => setInstallmentModalVisible(false)}
                  className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-bold hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleRecordInstallment}
                  disabled={recordingInstallment || !installmentAmount}
                  className="flex-1 bg-indigo-600 text-white py-3 rounded-xl font-bold hover:bg-indigo-700 transition-colors disabled:opacity-50 shadow-lg shadow-indigo-100"
                >
                  {recordingInstallment ? 'Recording...' : 'Record Payment'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Curriculum Modal */}
      {curriculumModalVisible && (
        <div className="fixed inset-0 md:left-56 lg:left-64 bg-black/60 backdrop-blur-md flex items-center justify-center z-[100] p-0 md:p-4">
          <div className="bg-white shadow-2xl w-full h-full md:max-h-full max-w-6xl rounded-2xl overflow-hidden flex flex-col animate-in slide-in-from-bottom-4 duration-300">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Unit Content & Lesson Plan</h2>
                <p className="text-base text-gray-600">{selectedUnitForCurriculum?.name}</p>
              </div>
              <button
                onClick={() => setCurriculumModalVisible(false)}
                className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              {/* Automated Generation & Data Ingestion Tool */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                {/* Complexity Strategy Selector */}
                <div className="md:col-span-2 mb-2 p-1 bg-gray-200/50 rounded-2xl flex gap-1 ring-1 ring-gray-200">
                  <button
                    onClick={() => setAiComplexityMode('compact')}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-black transition-all flex items-center justify-center gap-2 ${aiComplexityMode === 'compact' ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    <Clock className="w-3.5 h-3.5" />
                    Compact Essentials
                  </button>
                  <button
                    onClick={() => setAiComplexityMode('comprehensive')}
                    className={`flex-1 py-2 px-3 rounded-lg text-sm font-black transition-all flex items-center justify-center gap-2 ${aiComplexityMode === 'comprehensive' ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-100' : 'text-gray-500 hover:text-gray-700'}`}
                  >
                    <Trophy className="w-3.5 h-3.5" />
                    Academic Mastery
                  </button>
                </div>

                {/* PDF Ingestion */}
                <div className="bg-purple-50 p-4 rounded-xl border border-purple-100 shadow-sm">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-purple-900 flex items-center text-base">
                      <FileText className="w-3.5 h-3.5 mr-2 text-purple-600" />
                      Ingest Curriculum (PDF)
                    </h4>
                  </div>
                  <p className="text-xs text-purple-700 mb-3">
                    Upload your unit syllabus or textbook content for the Intelligent Tutor to learn.
                  </p>
                  <label className={`w-full py-2 flex items-center justify-center gap-2 rounded-lg cursor-pointer transition-all border border-dashed ${uploadingCurriculum ? 'bg-gray-100 border-gray-300' : 'bg-white border-purple-300 hover:bg-purple-100'}`}>
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={handleUploadCurriculum}
                      disabled={uploadingCurriculum}
                      className="hidden"
                    />
                    {uploadingCurriculum ? (
                      <div className="flex items-center gap-2 text-sm font-bold text-gray-500">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-gray-500"></div>
                        Uploading...
                      </div>
                    ) : (
                      <span className="text-sm font-bold text-purple-700 flex items-center gap-2">
                        <Plus className="w-3.5 h-3.5" />
                        Upload PDF
                      </span>
                    )}
                  </label>
                </div>

                {/* Course Planner */}
                <div className="bg-blue-50 p-4 rounded-xl border border-blue-100 shadow-sm flex flex-col h-full">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-blue-900 flex items-center text-base">
                      <Zap className="w-3.5 h-3.5 mr-2 text-yellow-500" />
                      Intelligent Course Builder
                    </h4>
                  </div>
                  <p className="text-xs text-blue-700 mb-3">
                    Auto-generate a comprehensive course (lessons, markdown notes, and YouTube materials) organically from a topic prompt.
                  </p>

                  <textarea
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    placeholder="E.g. Introduction to Python Data Structures"
                    className="w-full text-sm p-2 mb-2 rounded-lg border border-blue-200 outline-none focus:ring-2 focus:ring-blue-500 flex-1 min-h-[60px]"
                  />
                  <button
                    onClick={() => handleAICurriculum(selectedUnitForCurriculum.id)}
                    disabled={generatingAutomated || !aiPrompt.trim()}
                    className="w-full text-xs bg-blue-600 text-white px-3 py-2.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-1 font-bold transition-all"
                  >
                    {generatingAutomated ? (
                      <span className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                        Generating (up to 2 mins)...
                      </span>
                    ) : '✨ Construct Full Course'}
                  </button>
                </div>
              </div>

              {/* Add New Item Form */}
              <form onSubmit={handleAddCurriculumItem} className="space-y-3 bg-gray-50 p-4 rounded-xl border border-gray-200">
                <div className="grid grid-cols-3 gap-3">
                  <input
                    name="title"
                    placeholder="Section Title"
                    required
                    className="p-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none col-span-1"
                  />
                  <select
                    name="type"
                    className="p-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  >
                    <option value="curriculum">Curriculum Point</option>
                    <option value="schedule">Schedule Event</option>
                    <option value="lesson_plan">Lesson Goal</option>
                    <option value="outline">Module Outline</option>
                  </select>
                  <input
                    name="plannedDate"
                    type="date"
                    className="p-2 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                  />
                </div>
                <button
                  type="submit"
                  disabled={savingCurriculum}
                  className="w-full py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors text-base font-medium"
                >
                  {savingCurriculum ? 'Saving...' : '+ Add Section'}
                </button>
              </form>

              {/* Current Items List */}
              <div className="space-y-3">
                <h4 className="font-bold text-gray-800 text-base">Course Outline ({curriculumItems.length} steps)</h4>
                {fetchingCurriculum ? (
                  <div className="text-center py-6">Loading...</div>
                ) : (
                  <div className="space-y-4">
                    {curriculumItems.map((item, index) => (
                      <div key={item.id} className={`bg-white border rounded-2xl shadow-sm transition-all overflow-hidden ${item.isLive ? 'border-orange-200 shadow-orange-100/50 shadow-lg' :
                        item.isCompleted ? 'border-green-100' : 'border-gray-200'
                        }`}>

                        {/* Top Header Row */}
                        <div className={`p-4 border-b ${item.isLive ? 'bg-orange-50/30' : item.isCompleted ? 'bg-green-50/10' : 'bg-gray-50/30'} flex items-start gap-4`}>
                          <div className={`flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-md font-black text-xs ${item.isLive ? 'bg-orange-600 text-white' :
                            item.isCompleted ? 'bg-green-600 text-white' : 'bg-blue-50 text-blue-600'
                            }`}>
                            {item.isLive ? '🔴' : item.isCompleted ? <CheckCircle className="w-3.5 h-3.5" /> : index + 1}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex justify-between items-start gap-4">
                              <h5 className={`font-black text-base leading-tight ${item.isLive ? 'text-orange-900' : item.isCompleted ? 'text-green-900' : 'text-gray-900'}`}>{item.title}</h5>
                              <button onClick={() => handleDeleteCurriculumItem(item.id)} className="text-gray-300 hover:text-red-500 flex-shrink-0">
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                            <div className="flex flex-wrap items-center gap-2 mt-1.5">
                              <span className="text-xs text-gray-400 font-bold uppercase tracking-widest">{item.type.replace('_', ' ')}</span>
                              {item.isCompleted && (
                                <span className="text-xs text-green-600 font-bold bg-green-50 px-2 py-0.5 rounded-full">Done: {new Date(item.completedAt).toLocaleDateString()}</span>
                              )}
                              {item.isLive && item.liveStartedAt && (
                                <span className="text-xs bg-orange-600 text-white px-2 py-0.5 rounded-full font-black uppercase tracking-wide animate-pulse">Live Now</span>
                              )}
                              {item.plannedDate && (
                                <span className="text-xs text-gray-400">Target: {new Date(item.plannedDate).toLocaleDateString()}</span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* 3-Column Content Row */}
                        <div className="flex flex-col md:flex-row">
                          {/* Column 1: Actions */}
                          <div className="w-full md:w-56 p-4 border-b md:border-b-0 md:border-r border-gray-100 flex flex-col gap-2 justify-start">
                            {item.isLive ? (
                              <button onClick={() => handleEndLesson(item.id)} className="w-full text-sm font-black px-4 py-2 rounded-full bg-red-600 text-white hover:bg-red-700 transition-colors uppercase tracking-widest">
                                End Lesson
                              </button>
                            ) : (
                              <button
                                onClick={() => handleStartLesson(item.id)}
                                disabled={selectedUnitForCurriculum?.currentCohortStatus !== 'active'}
                                className={`w-full text-sm font-black px-4 py-2 rounded-full transition-colors uppercase tracking-widest ${selectedUnitForCurriculum?.currentCohortStatus === 'active' ? 'bg-gray-800 text-white hover:bg-gray-900' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                  }`}
                              >
                                Attend Lesson
                              </button>
                            )}

                            <button onClick={() => handleToggleCompletion(item.id, !item.isCompleted)} className={`w-full text-sm font-black px-4 py-2 rounded-full border transition-colors uppercase tracking-widest ${item.isCompleted ? 'bg-green-100 border-green-200 text-green-800 hover:bg-green-200' : 'bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100'
                              }`}>
                              {item.isCompleted ? 'Mark Undone' : 'Mark as Done'}
                            </button>

                            <button onClick={() => handleAIExplain(item.id)} disabled={explainingId === item.id} className={`w-full text-xs font-bold px-3 py-1.5 rounded-full border flex items-center justify-center gap-1.5 transition-all ${explainingId === item.id ? 'bg-purple-100 text-purple-700 border-purple-300 animate-pulse' : 'bg-white text-indigo-600 border-indigo-200 hover:bg-indigo-50'
                              }`}>
                              <Bot className="w-3 h-3" /> {explainingId === item.id ? 'Explaining...' : 'Tutor Insight'}
                            </button>

                            <button onClick={() => handleGenerateAIQuiz(item.id)} disabled={generatingQuizId === item.id} className={`w-full text-xs font-bold px-3 py-1.5 rounded-full border flex items-center justify-center gap-1.5 transition-all ${generatingQuizId === item.id ? 'bg-yellow-100 text-yellow-700 border-yellow-300 animate-pulse' : 'bg-white text-green-600 border-green-200 hover:bg-green-50'
                              }`}>
                              <CheckCircle className="w-3 h-3" /> {generatingQuizId === item.id ? 'Generating Quiz...' : 'Create Smart Quiz'}
                            </button>
                          </div>

                          {/* Column 2: Lesson Content Preview */}
                          <div className="flex-1 p-4 border-b md:border-b-0 md:border-r border-gray-100 flex flex-col justify-center">
                            {item.description ? (
                              <button
                                onClick={() => handleViewLessonContent(item)}
                                className="w-full text-left bg-white border border-indigo-100 rounded-xl p-4 hover:shadow-md transition-all group"
                              >
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-xs font-black text-indigo-600 uppercase tracking-widest flex items-center gap-1.5">
                                    <BookOpen className="w-3 h-3" /> Lesson Content
                                  </span>
                                  <span className="text-xs text-indigo-400 group-hover:text-indigo-600 font-bold whitespace-nowrap">Click to enlarge &rarr;</span>
                                </div>
                                <p className="text-sm text-gray-500 line-clamp-3 leading-relaxed font-medium">
                                  {item.description.replace(/[#*`>]/g, '').slice(0, 180)}...
                                </p>
                              </button>
                            ) : (
                              <div className="h-full flex items-center justify-center p-6 border-2 border-dashed border-gray-100 rounded-xl text-center">
                                <div>
                                  <BookOpen className="w-6 h-6 text-gray-300 mx-auto mb-2" />
                                  <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">No Lesson Content</p>
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Column 3: Resources */}
                          <div className="w-full md:w-64 p-4 flex flex-col min-h-full">
                            <p className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3">Lesson Resources</p>

                            <div className="flex-1 mb-4">
                              <div className="flex flex-col gap-1.5">
                                {item.assignments?.map(a => (
                                  <div key={`a-${a.id}`} className="flex items-center gap-1.5 text-blue-700 text-sm font-bold truncate">
                                    <FileText className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{a.title}</span>
                                  </div>
                                ))}
                                {item.quizzes?.map(q => (
                                  <div key={`q-${q.id}`} className="flex items-center gap-1.5 text-green-700 text-sm font-bold truncate">
                                    <CheckCircle className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{q.title ? q.title.replace(/AI Quiz:?/gi, 'Smart Quiz:') : ''}</span>
                                  </div>
                                ))}
                                {item.materials?.map(m => (
                                  <div key={`m-${m.id}`} className="flex items-center gap-1.5 text-purple-700 text-sm font-bold truncate">
                                    <BookOpen className="w-3 h-3 flex-shrink-0" /> <span className="truncate">{m.title}</span>
                                  </div>
                                ))}
                                {(!item.assignments?.length && !item.quizzes?.length && !item.materials?.length) && (
                                  <span className="text-sm text-gray-400 font-medium italic">No resources attached</span>
                                )}
                              </div>
                            </div>

                            <div className="flex items-center gap-1 mt-auto pt-3 border-t border-gray-50 flex-wrap">
                              <button onClick={() => navigate(`/assignments?unitId=${selectedUnitForCurriculum.id}&action=create_assignment&lessonItemId=${item.id}`)} className="flex-1 text-xs font-black bg-blue-600 text-white px-0 py-1.5 rounded hover:bg-blue-700 flex justify-center items-center gap-1 transition-colors">
                                <Plus className="w-3 h-3" /> Assignment
                              </button>
                              <button onClick={() => navigate(`/assignments?unitId=${selectedUnitForCurriculum.id}&action=create_quiz&lessonItemId=${item.id}`)} className="flex-1 text-xs font-black bg-green-600 text-white px-0 py-1.5 rounded hover:bg-green-700 flex justify-center items-center gap-1 transition-colors">
                                <Plus className="w-3 h-3" /> Quiz
                              </button>
                              <button onClick={() => navigate(`/learning-materials?unitId=${selectedUnitForCurriculum.id}&action=upload&lessonItemId=${item.id}`)} className="flex-1 text-xs font-black bg-purple-600 text-white px-0 py-1.5 rounded hover:bg-purple-700 flex justify-center items-center gap-1 transition-colors">
                                <Plus className="w-3 h-3" /> Material
                              </button>
                            </div>
                          </div>
                        </div>

                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-4 border-t border-gray-100 bg-gray-50 text-right">
                <button
                  onClick={() => setCurriculumModalVisible(false)}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Lesson Content Viewer / Editor Modal */}
      {lessonContentModal && (
        <div className="fixed inset-0 md:left-56 lg:left-64 bg-black/60 backdrop-blur-md flex items-center justify-center z-[110]" onClick={() => setLessonContentModal(null)}>
          <div className="bg-white shadow-2xl w-full h-full md:max-h-full max-w-5xl rounded-2xl flex flex-col overflow-hidden animate-in slide-in-from-bottom-4 duration-300" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-start justify-between p-5 border-b border-gray-100 bg-gradient-to-r from-indigo-600 to-purple-700">
              <div className="flex-1 min-w-0">
                <span className="text-xs font-bold text-indigo-200 uppercase tracking-widest">Lesson Notes</span>
                <h3 className="text-white font-black text-xl mt-0.5 leading-tight truncate">{lessonContentModal.title}</h3>
              </div>
              <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                <button
                  onClick={() => { setEditMode(!editMode); if (!editMode) setEditingContent(lessonContentModal.description || ''); }}
                  className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-all ${editMode ? 'bg-white text-indigo-700' : 'bg-white/20 text-white hover:bg-white/30'
                    }`}
                >
                  {editMode ? '👁 View' : '✏️ Edit'}
                </button>
                <button onClick={() => setLessonContentModal(null)} className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors">
                  <X className="w-4 h-4 text-white" />
                </button>
              </div>
            </div>

            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-6">
              {editMode ? (
                <textarea
                  value={editingContent}
                  onChange={e => setEditingContent(e.target.value)}
                  className="w-full h-full text-base text-gray-800 font-mono bg-gray-50 border border-gray-200 rounded-xl p-4 resize-none focus:ring-2 focus:ring-indigo-300 focus:border-indigo-400 outline-none leading-relaxed"
                  placeholder="Write or paste lesson markdown content here..."
                />
              ) : (
                <div className="prose max-w-none text-gray-800 space-y-1">
                  {(lessonContentModal.description || '').split('\n').map((line, i) => {
                    if (line.startsWith('### ')) return <h3 key={i} className="text-base font-bold text-gray-800 mt-4 mb-1">{line.slice(4)}</h3>;
                    if (line.startsWith('## ')) return <h2 key={i} className="text-lg font-extrabold text-indigo-900 mt-5 mb-2 pb-1 border-b border-indigo-100">{line.slice(3)}</h2>;
                    if (line.startsWith('# ')) return <h1 key={i} className="text-xl font-black text-indigo-900 mt-4 mb-3">{line.slice(2)}</h1>;
                    if (line.startsWith('- ') || line.startsWith('* ')) return <li key={i} className="ml-4 text-base text-gray-700 mb-0.5 list-disc">{line.slice(2)}</li>;
                    if (/^\d+\. /.test(line)) return <li key={i} className="ml-4 text-base text-gray-700 mb-0.5 list-decimal">{line.replace(/^\d+\. /, '')}</li>;
                    if (line === '---') return <hr key={i} className="border-gray-200 my-3" />;
                    if (line.trim() === '') return <div key={i} className="h-1.5" />;
                    return <p key={i} className="text-base text-gray-700 mb-1 leading-relaxed">{line.replace(/\*\*(.+?)\*\*/g, '$1')}</p>;
                  })}
                </div>
              )}
            </div>

            {/* Intelligent Refine Panel - Pinned to bottom above footer */}
            <div className="px-6 py-6 border-t border-indigo-50 bg-gradient-to-br from-purple-50/50 to-indigo-50/50">
              <h4 className="text-sm font-black text-purple-800 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                <Bot className="w-3.5 h-3.5" />
                Refine with Intelligent Tutor
              </h4>
              <p className="text-xs text-purple-600 mb-3">Give an instruction to the Intelligent Tutor to rewrite or improve this lesson content. Changes are saved automatically.</p>
              <div className="flex gap-2">
                <input
                  value={refinementPrompt}
                  onChange={e => setRefinementPrompt(e.target.value)}
                  placeholder='E.g. "Make it more concise" or "Add more practical examples"'
                  className="flex-1 text-sm border border-purple-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-purple-400 outline-none bg-white"
                  onKeyDown={e => e.key === 'Enter' && !refiningContent && handleAIRefineContent()}
                />
                <button
                  onClick={handleAIRefineContent}
                  disabled={refiningContent || !refinementPrompt.trim()}
                  className="text-sm font-bold px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-1.5 transition-colors whitespace-nowrap"
                >
                  {refiningContent ? (
                    <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Refining...</>
                  ) : (
                    <><Bot className="w-3 h-3" /> Refine</>
                  )}
                </button>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50">
              <p className="text-xs text-gray-400">
                {lessonContentModal.description ? `${lessonContentModal.description.length.toLocaleString()} characters` : 'No content yet'}
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setLessonContentModal(null)}
                  className="text-sm font-medium px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
                >
                  Close
                </button>
                {editMode && (
                  <button
                    onClick={handleSaveContent}
                    disabled={savingContent}
                    className="text-sm font-bold px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-60 flex items-center gap-2 transition-colors"
                  >
                    {savingContent ? <><div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</> : '💾 Save Changes'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* ─── COHORT MANAGEMENT MODAL ────────────────────────────────────────── */}
      {cohortModalVisible && (
        <div className="fixed inset-0 md:left-56 lg:left-64 bg-black/60 backdrop-blur-md flex items-center justify-center z-[90] p-0 md:p-4">
          <div className="bg-white shadow-2xl w-full max-w-6xl h-full md:max-h-full rounded-2xl overflow-hidden flex flex-col">

            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-700 p-5 flex items-start justify-between">
              <div>
                <span className="text-xs font-bold text-indigo-200 uppercase tracking-widest">Cohort Manager</span>
                <h2 className="text-white font-black text-xl mt-0.5">{cohortModalUnit?.name}</h2>
                {activeCohort && (
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                      activeCohort.status === 'active' ? 'bg-green-400/30 text-green-100' :
                      activeCohort.status === 'forming' ? 'bg-yellow-400/30 text-yellow-100' :
                      'bg-gray-400/30 text-gray-200'
                    }`}>{activeCohort.label || `Cohort #${activeCohort.cohortNumber}`} • {activeCohort.status.toUpperCase()}</span>
                    {activeCohort.status === 'active' && activeCohort.expectedEndDate && (
                      <span className="text-xs text-indigo-200">
                        Ends {new Date(activeCohort.expectedEndDate).toLocaleDateString()}
                        {activeCohort.daysRemaining !== null && ` (· ${activeCohort.daysRemaining}d left)`}
                      </span>
                    )}
                  </div>
                )}
              </div>
              <button onClick={() => setCohortModalVisible(false)} className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors">
                <X className="w-5 h-5 text-white" />
              </button>
            </div>

            {/* Tab Bar */}
            <div className="flex border-b border-gray-100 bg-gray-50">
              {['progress', 'students', 'history'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setCohortModalTab(tab)}
                  className={`flex-1 py-3 text-sm font-bold transition-colors flex items-center justify-center gap-2 ${
                    cohortModalTab === tab ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab === 'progress' ? <><BarChart2 className="w-4 h-4" /> Live Wall</> : 
                   tab === 'students' ? <><Users className="w-4 h-4" /> Students & Payments</> :
                   <><History className="w-4 h-4" /> History</>}
                </button>
              ))}
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5">
              {fetchingCohort ? (
                <div className="flex items-center justify-center py-16">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
                  <span className="ml-3 text-gray-600">Loading cohort data...</span>
                </div>
              ) : cohortModalTab === 'progress' ? (
                <div className="space-y-5">

                  {/* Summary Bar */}
                  {cohortModalUnit?.compensationMethod !== 'salary' && (
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border border-blue-100 shadow-sm">
                      <div>
                        <p className="text-xs font-bold text-blue-700 uppercase tracking-widest mb-1">Your Expected Income</p>
                        <p className="text-2xl font-black text-blue-900">
                          KES {(allUnitStudents.reduce((acc, curr) => acc + (parseFloat(curr.paidAmount) || 0), 0) * (cohortModalUnit?.lecturerPercentage / 100 || 0.60)).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* No cohort — prompt to create */}
                  {!activeCohort ? (
                    <div className="text-center py-10 border-2 border-dashed border-indigo-200 rounded-2xl bg-indigo-50/30">
                      <Award className="w-12 h-12 text-indigo-300 mx-auto mb-3" />
                      <h3 className="font-bold text-gray-800 text-lg mb-1">No Active Cohort</h3>
                      <p className="text-sm text-gray-500 mb-5">Create a cohort to start tracking students for this unit.</p>
                      {showCreateCohort ? (
                        <div className="bg-white rounded-xl border border-indigo-100 p-5 text-left space-y-4 max-w-sm mx-auto shadow-sm">
                          <h4 className="font-bold text-gray-900">New Cohort Setup</h4>
                          <div>
                            <label className="text-xs font-bold text-gray-600 block mb-1">Label (optional)</label>
                            <input
                              type="text"
                              placeholder="e.g. Jan 2026 Intake"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
                              value={newCohortForm.label}
                              onChange={e => setNewCohortForm(p => ({ ...p, label: e.target.value }))}
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs font-bold text-gray-600 block mb-1">Max Students</label>
                              <input type="number" min="1" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
                                value={newCohortForm.maxSize}
                                onChange={e => setNewCohortForm(p => ({ ...p, maxSize: parseInt(e.target.value) }))}
                              />
                            </div>
                            <div>
                              <label className="text-xs font-bold text-gray-600 block mb-1">Est. Duration (days)</label>
                              <input type="number" min="7" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 outline-none"
                                value={newCohortForm.estimatedDurationDays}
                                onChange={e => setNewCohortForm(p => ({ ...p, estimatedDurationDays: parseInt(e.target.value) }))}
                              />
                            </div>
                          </div>
                          {/* Auto-assign option — only show if there are unassigned students */}
                          {getAssignableStudents().length > 0 && (
                            <label className="flex items-start gap-2 cursor-pointer p-2 bg-amber-50 rounded-lg border border-amber-200">
                              <input
                                type="checkbox"
                                className="mt-0.5 accent-indigo-600"
                                checked={newCohortForm.autoAssignUnassigned}
                                onChange={e => setNewCohortForm(p => ({ ...p, autoAssignUnassigned: e.target.checked }))}
                              />
                              <span className="text-xs text-amber-900 font-bold leading-tight">
                                Auto-assign {getAssignableStudents().length} student(s) who have already paid but are not in a cohort
                              </span>
                            </label>
                          )}
                          <div className="flex gap-3">
                            <button onClick={() => setShowCreateCohort(false)} className="flex-1 py-2 text-sm font-bold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors">Cancel</button>
                            <button onClick={handleCreateCohort} disabled={creatingCohort} className="flex-1 py-2 text-sm font-bold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors">
                              {creatingCohort ? 'Creating...' : 'Create Cohort'}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <button onClick={() => setShowCreateCohort(true)} className="px-6 py-2.5 bg-indigo-600 text-white rounded-xl font-bold hover:bg-indigo-700 transition-colors shadow-lg shadow-indigo-100 flex items-center gap-2 mx-auto">
                          <Plus className="w-4 h-4" /> Create First Cohort
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      {/* Cohort Controls */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <div className="bg-indigo-50 rounded-xl p-3 border border-indigo-100">
                          <p className="text-xs text-indigo-600 font-bold uppercase mb-1">Students</p>
                          <p className="text-2xl font-black text-indigo-900">{activeCohort.studentCount ?? cohortStudents.length}</p>
                          <p className="text-xs text-indigo-500">/ {activeCohort.maxSize} max</p>
                        </div>
                        <div className="bg-green-50 rounded-xl p-3 border border-green-100">
                          <p className="text-xs text-green-600 font-bold uppercase mb-1">Avg. Progress</p>
                          <p className="text-2xl font-black text-green-900">
                            {cohortStudents.length > 0
                              ? Math.round(cohortStudents.reduce((a, s) => a + (s.progressPercentage || 0), 0) / cohortStudents.length)
                              : 0}%
                          </p>
                        </div>
                        <div className="bg-orange-50 rounded-xl p-3 border border-orange-100">
                          <p className="text-xs text-orange-600 font-bold uppercase mb-1">Behind</p>
                          <p className="text-2xl font-black text-orange-900">{cohortStudents.filter(s => s.isBehind).length}</p>
                          <p className="text-xs text-orange-500">students</p>
                        </div>
                        <div className={`rounded-xl p-3 border ${activeCohort.admissionsOpen ? 'bg-teal-50 border-teal-100' : 'bg-red-50 border-red-100'}`}>
                          <p className={`text-xs font-bold uppercase mb-1 ${activeCohort.admissionsOpen ? 'text-teal-600' : 'text-red-600'}`}>Admissions</p>
                          <p className={`text-sm font-black ${activeCohort.admissionsOpen ? 'text-teal-900' : 'text-red-900'}`}>
                            {activeCohort.admissionsOpen ? 'OPEN' : 'CLOSED'}
                          </p>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex flex-wrap gap-2">
                        {activeCohort.status === 'forming' && (
                          <>
                            <button onClick={() => handleStartCohort(false)} disabled={startingCohort}
                              className="px-4 py-2 text-sm font-bold bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2 transition-colors">
                              <CheckCircle className="w-4 h-4" /> {startingCohort ? 'Starting...' : 'Start Cohort'}
                            </button>
                            <button onClick={() => handleStartCohort(true)} disabled={startingCohort}
                              className="px-4 py-2 text-sm font-bold bg-green-700 text-white rounded-lg hover:bg-green-800 disabled:opacity-50 flex items-center gap-2 transition-colors">
                              <Lock className="w-4 h-4" /> Start & Close Admissions
                            </button>
                          </>
                        )}
                        <button onClick={handleToggleAdmissions} disabled={togglingAdmissions}
                          className={`px-4 py-2 text-sm font-bold rounded-lg flex items-center gap-2 transition-colors disabled:opacity-50 ${
                            activeCohort.admissionsOpen
                              ? 'bg-red-100 text-red-700 hover:bg-red-200'
                              : 'bg-teal-100 text-teal-700 hover:bg-teal-200'
                          }`}>
                          {activeCohort.admissionsOpen ? <><Lock className="w-4 h-4" /> Close Admissions</> : <><Unlock className="w-4 h-4" /> Open Admissions</>}
                        </button>
                        {activeCohort.status === 'active' && (
                          <button onClick={handleGraduateCohort} disabled={graduatingCohort}
                            className="px-4 py-2 text-sm font-bold bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 transition-colors ml-auto">
                            <Award className="w-4 h-4" /> {graduatingCohort ? 'Graduating...' : 'Graduate & Archive Cohort'}
                          </button>
                        )}
                      </div>

                      {/* Progress Wall */}
                      {cohortStudents.length > 0 ? (
                        <div className="space-y-3">
                          <h4 className="font-bold text-gray-800 flex items-center gap-2">
                            <BarChart2 className="w-4 h-4 text-indigo-500" /> Student Progress Wall
                          </h4>
                          {cohortStudents.map(s => (
                            <div key={s.studentId} className={`bg-white rounded-xl border p-4 ${
                              s.isBehind ? 'border-orange-200 bg-orange-50/30' : 'border-gray-100'
                            }`}>
                              <div className="flex items-center justify-between mb-2">
                                <div>
                                  <p className="font-bold text-gray-900 text-sm">{s.studentName}</p>
                                  <p className="text-xs text-gray-500">{s.studentEmail}</p>
                                </div>
                                <div className="flex items-center gap-2">
                                  {s.isBehind && (
                                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded-full border border-orange-200">Behind</span>
                                  )}
                                  <span className={`text-sm font-black ${
                                    s.progressPercentage >= 75 ? 'text-green-600' :
                                    s.progressPercentage >= 40 ? 'text-blue-600' : 'text-gray-500'
                                  }`}>{s.progressPercentage}%</span>
                                </div>
                              </div>
                              <div className="w-full bg-gray-100 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full transition-all ${
                                    s.isBehind ? 'bg-orange-400' :
                                    s.progressPercentage >= 75 ? 'bg-green-500' : 'bg-indigo-500'
                                  }`}
                                  style={{ width: `${s.progressPercentage}%` }}
                                />
                              </div>
                              <div className="flex justify-between mt-1">
                                <span className="text-xs text-gray-400">{s.completedLessons}/{s.totalLessons} lessons</span>
                                <span className="text-xs text-gray-400">Expected: {s.expectedProgress}%</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                          <Users className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                          <p className="text-gray-500 text-sm">No students in this cohort yet.</p>
                          <p className="text-gray-400 text-xs mt-1">Students auto-join when their payment is confirmed.</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : cohortModalTab === 'students' ? (
                /* Students & Payments Tab */
                <div className="space-y-4">
                  {/* Unassigned students warning */}
                  {(() => {
                    const unassignedCount = getAssignableStudents().length;
                    return unassignedCount > 0 ? (
                      <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-xl p-3">
                        <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                        <p className="text-xs font-bold text-amber-800 flex-1">
                          ⚠ {unassignedCount} student{unassignedCount > 1 ? 's have' : ' has'} paid but {unassignedCount > 1 ? 'are' : 'is'} not in any cohort.
                        </p>
                        {!activeCohort && (
                          <span className="text-[10px] text-amber-600 font-bold">Create a cohort to assign them.</span>
                        )}
                      </div>
                    ) : null;
                  })()}
                   {fetchingUnitStudents ? (
                    <div className="flex justify-center items-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    </div>
                  ) : allUnitStudents.length > 0 ? (
                    allUnitStudents.map((su) => {
                      const graduated = isGraduatedStudent(su);
                      const canReleaseFunds = su.canReleaseFunds === true && !graduated;
                      return (
                      <div key={su.id || su.studentId} className="border border-gray-100 rounded-xl p-4 bg-white hover:shadow-sm transition-shadow flex items-start gap-4">
                        {su.student?.profilePicture ? (
                          <img src={getImageUrl(su.student.profilePicture)} alt={su.student.name} className="w-12 h-12 rounded-full object-cover border-2 border-gray-50" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-gray-50 flex items-center justify-center border-2 border-gray-100">
                            <User className="w-6 h-6 text-gray-300" />
                          </div>
                        )}
                        <div className="flex-1">
                          <div className="flex justify-between items-start">
                            <div>
                              <h4 className="font-bold text-gray-900">{su.student?.name}</h4>
                              <p className="text-xs text-gray-500">{su.student?.email}</p>
                              <div className="flex flex-wrap items-center gap-2 mt-2">
                                <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-tighter ${
                                  su.paymentStatus === 'confirmed' ? 'bg-green-100 text-green-700' :
                                  su.paymentStatus === 'partial' ? 'bg-blue-100 text-blue-700' : 'bg-yellow-100 text-yellow-700'
                                }`}>
                                  {su.paymentStatus}
                                </span>
                                {graduated && (
                                  <span className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-tighter bg-purple-100 text-purple-700">
                                    Graduated
                                  </span>
                                )}
                                <span className="text-xs font-bold text-gray-600">KES {parseFloat(su.paidAmount || 0).toLocaleString()} / {parseFloat(su.totalAmount || su.paymentAmount || 0).toLocaleString()}</span>
                                {su.latestPayment?.status === 'pending' && (
                                  <span className="text-[10px] bg-orange-100 text-orange-700 px-2 py-0.5 rounded font-bold animate-pulse">
                                    PENDING {su.latestPayment.paymentMethod?.toUpperCase()}: KES {parseFloat(su.latestPayment.amount).toLocaleString()}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-col gap-2">
                                {su.latestPayment?.paymentMethod === 'cash' && su.latestPayment?.status === 'pending' && (
                                <div className="flex gap-1">
                                  <button onClick={() => handleApproveCashPayment(su.studentId, su.unitId)} className="bg-green-600 text-white px-2 py-1 rounded text-xs font-bold hover:bg-green-700">Approve Cash</button>
                                  <button onClick={() => handleRejectCashPayment(su.studentId, su.unitId)} className="bg-red-600 text-white px-2 py-1 rounded text-xs font-bold hover:bg-red-700">Reject</button>
                                </div>
                              )}
                              {su.paymentStatus !== 'confirmed' && (
                                <button onClick={() => { setSelectedStudentForInstallment(su); setInstallmentModalVisible(true); }} className="bg-blue-600 text-white px-3 py-1 rounded text-xs font-bold hover:bg-blue-700 flex items-center gap-1">
                                  <Plus className="w-3 h-3" /> Installment
                                </button>
                              )}
                              {su.paymentStatus === 'confirmed' && (
                                !graduated ? (
                                  <button
                                    onClick={() => canReleaseFunds && handleReleaseFunds(su.studentId, su.unitId)}
                                    disabled={!canReleaseFunds || releasingFundsId === `${su.studentId}-${su.unitId}`}
                                    title={!canReleaseFunds ? 'Only the lecturer assigned to this student/cohort can release these funds.' : undefined}
                                    className="bg-indigo-600 text-white px-3 py-1 rounded text-xs font-bold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {!canReleaseFunds ? 'Not Your Cohort' :
                                     releasingFundsId === `${su.studentId}-${su.unitId}` ? '...' : 
                                     cohortModalUnit?.compensationMethod === 'salary' ? 'Complete Unit' : 'Release Funds'}
                                  </button>
                                ) : (
                                  <span className="bg-gray-100 text-gray-500 px-3 py-1 rounded text-xs font-bold border border-gray-200">
                                    Graduated
                                  </span>
                                )
                              )}
                              {/* Manual Assign to Cohort button */}
                              {!su.cohortRunId && ['confirmed', 'partial'].includes(su.paymentStatus) && !graduated && (
                                <button
                                  onClick={() => handleAssignStudent(su.studentId, su.student?.name || 'Student')}
                                  disabled={assigningStudentId === su.studentId || !activeCohort}
                                  title={!activeCohort ? 'Create a cohort first' : `Assign to Cohort #${activeCohort?.cohortNumber}`}
                                  className="bg-teal-600 text-white px-2 py-1 rounded text-xs font-bold hover:bg-teal-700 disabled:opacity-40 flex items-center gap-1"
                                >
                                  {assigningStudentId === su.studentId ? '...' : '+ Assign to Cohort'}
                                </button>
                              )}
                            </div>
                          </div>
                          {graduated ? (
                            <div className="mt-3 py-1.5 px-3 bg-purple-50 rounded-lg border border-purple-100 inline-flex items-center gap-2">
                               <Award className="w-3 h-3 text-purple-500" />
                               <span className="text-[10px] font-bold text-purple-600 uppercase">Graduated from this unit</span>
                            </div>
                          ) : su.cohortRunId ? (
                            <div className="mt-3 py-1.5 px-3 bg-gray-50 rounded-lg border border-gray-100 inline-flex items-center gap-2">
                               <Award className="w-3 h-3 text-indigo-500" />
                               <span className="text-[10px] font-bold text-gray-500 uppercase">Assigned to Cohort {su.cohortRun?.cohortNumber || 'active'}</span>
                            </div>
                          ) : (
                            <div className="mt-3 py-1.5 px-3 bg-red-50 rounded-lg border border-red-100 inline-flex items-center gap-2">
                               <AlertCircle className="w-3 h-3 text-red-500" />
                               <span className="text-[10px] font-bold text-red-500 uppercase">Not in any cohort</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )})
                  ) : (
                    <div className="text-center py-12 text-gray-400">No students found for this unit.</div>
                  )}
                </div>
              ) : (
                /* History Tab */
                <div className="space-y-4">
                  {cohortHistory.length === 0 ? (
                    <div className="text-center py-12 text-gray-500">
                      <History className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      No archived cohorts yet.
                    </div>
                  ) : cohortHistory.map(c => (
                    <div key={c.id} className="bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-bold text-gray-900">{c.label || `Cohort #${c.cohortNumber}`}</h4>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Started: {c.startedAt ? new Date(c.startedAt).toLocaleDateString() : '—'} &nbsp;·&nbsp;
                            Archived: {c.archivedAt ? new Date(c.archivedAt).toLocaleDateString() : '—'}
                          </p>
                        </div>
                        <span className={`px-2.5 py-1 text-xs font-bold rounded-full ${
                          c.status === 'archived' ? 'bg-gray-100 text-gray-600' : 'bg-green-100 text-green-700'
                        }`}>{c.status}</span>
                      </div>
                      <div className="flex items-center gap-3 mb-3">
                        <span className="flex items-center gap-1 text-sm text-gray-700"><Users className="w-4 h-4" /> {c.studentCount} graduates</span>
                      </div>
                      {c.graduates?.length > 0 && (
                        <div className="space-y-2 max-h-48 overflow-y-auto">
                          {c.graduates.map(g => (
                            <div key={g.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 text-sm">
                              <span className="font-medium text-gray-800">{g.student?.name}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">{g.progressPercentage}%</span>
                                <span className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                                  g.finalStatus === 'completed' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                                }`}>{g.finalStatus || 'enrolled'}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-100 bg-gray-50 flex justify-between items-center">
              <button
                onClick={() => !activeCohort && setShowCreateCohort(!showCreateCohort)}
                disabled={!!activeCohort}
                title={activeCohort ? 'Archive the current cohort before creating another one.' : undefined}
                className="text-sm font-bold text-indigo-600 hover:text-indigo-800 flex items-center gap-1 transition-colors disabled:text-gray-400 disabled:cursor-not-allowed">
                <Plus className="w-4 h-4" /> {activeCohort ? 'One Active Cohort' : 'New Cohort'}
              </button>
              <button onClick={() => setCohortModalVisible(false)} className="px-5 py-2 bg-gray-800 text-white rounded-lg text-sm font-bold hover:bg-gray-900 transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default LecturerUnits;
